import {useState, useEffect} from 'react'
import {supabase} from '../config/supabase'
import toast from 'react-hot-toast'

export const useSiteContent = () => {
  const [content, setContent] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchContent()

    // Subscribe to content changes
    const subscription = supabase
      .channel('site-content-changes')
      .on('postgres_changes', {event: '*', schema: 'public', table: 'site_content'}, () => fetchContent())
      .subscribe()

    return () => subscription.unsubscribe()
  }, [])

  const fetchContent = async () => {
    try {
      const {data, error} = await supabase
        .from('site_content')
        .select('*')
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      setContent(data || getDefaultContent())
    } catch (error) {
      console.error('Error fetching site content:', error)
      // Set default content if fetch fails
      setContent(getDefaultContent())
    } finally {
      setLoading(false)
    }
  }

  const updateContent = async (updates) => {
    try {
      // Check if content exists
      const {data: existing} = await supabase
        .from('site_content')
        .select('id')
        .limit(1)
        .single()

      let result
      if (existing) {
        // Update existing content
        result = await supabase
          .from('site_content')
          .update(updates)
          .eq('id', existing.id)
          .select()
          .single()
      } else {
        // Create new content
        result = await supabase
          .from('site_content')
          .insert([updates])
          .select()
          .single()
      }

      if (result.error) throw result.error

      setContent(result.data)
      toast.success('Site content updated successfully!')
      return {data: result.data, error: null}
    } catch (error) {
      console.error('Error updating site content:', error)
      toast.error('Failed to update site content')
      return {data: null, error}
    }
  }

  const getReviewsDisplayText = () => {
    if (!content) return '50+ verified reviews on Google'
    return content.reviews_display_text || '50+ verified reviews on Google'
  }

  const getPPCDefinition = () => {
    if (!content) return 'PPC stands for Protein, Produce, and Carbs - the foundations of sustainable nutrition'
    return content.howitworks_ppc_definition
  }

  const getFooterLinks = () => {
    if (!content) return getDefaultContent().footer_links
    return content.footer_links || []
  }

  return {
    content,
    loading,
    updateContent,
    getReviewsDisplayText,
    getPPCDefinition,
    getFooterLinks,
    refetch: fetchContent
  }
}

const getDefaultContent = () => ({
  id: 'default',
  hero_title: 'Sustainable Weight Loss for LIFE',
  hero_subtitle: 'Simple, sustainable habits for people with real lives.',
  cta_primary_label: 'Explore Courses',
  cta_primary_url: '/courses',
  cta_secondary_label: 'Book a Call',
  cta_secondary_url: 'https://tidycal.com/empowernutrition/30-minute-consultation',
  reviews_google_count: 51,
  reviews_display_text: '50+ verified reviews on Google',
  reviews_source_label: 'Google',
  transformation_cta_label: 'Book a Consultation',
  howitworks_ppc_definition: 'PPC stands for Protein, Produce, and Carbs - the foundations of sustainable nutrition',
  footer_links: [
    {label: 'Privacy Policy', url: '/privacy'},
    {label: 'Terms of Service', url: '/terms'},
    {label: 'Contact', url: 'mailto:laura@empowernutrition.info'}
  ]
})